import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { UserPlus, Loader2, AlertTriangle, Clock, Mail, CheckCircle2, FileCheck, Upload, X, Zap } from 'lucide-react';
import axios from 'axios';
import useSEO from '../hooks/useSEO';
import { COUNTRIES } from '../utils/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  useSEO({
    title: 'Registrazione',
    description: 'Registrati gratuitamente su LIFE LINKE. Crea un account con codice fiscale per segnalare emergenze, ricevere aggiornamenti e chattare con gli operatori di protezione civile.',
    keywords: 'registrazione lifelinke, iscriviti emergenza, account protezione civile',
  });
  const [form, setForm] = useState({
    nome: '', cognome: '', email: '', password: '',
    codice_fiscale: '', codice_iss: '', numero_passaporto: '', paese_passaporto: '',
    documento_identita: '', telefono: ''
  });
  const [idType, setIdType] = useState('cf');  // 'cf' | 'iss' | 'passport'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingState, setPendingState] = useState(null);
  const [fronte, setFronte] = useState(null); // { file, uploaded_id, progress }
  const [retro, setRetro] = useState(null);
  const fronteRef = useRef();
  const retroRef = useRef();

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadDoc = async (file, setter) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Il file supera i 10MB');
      return;
    }
    setter({ file, uploaded_id: null, uploading: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd);
      setter({ file, uploaded_id: data.id, uploading: false });
    } catch (e) {
      setError('Errore caricamento documento');
      setter(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nome || !form.cognome || !form.email || !form.password) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    if (idType === 'cf' && !form.codice_fiscale) {
      setError('Il Codice Fiscale è obbligatorio');
      return;
    }
    if (idType === 'iss' && !form.codice_iss) {
      setError('Il Codice ISS è obbligatorio');
      return;
    }
    if (idType === 'passport' && !form.numero_passaporto) {
      setError('Il Numero di Passaporto è obbligatorio');
      return;
    }
    if (idType === 'passport' && !form.paese_passaporto) {
      setError('Seleziona il paese di rilascio del passaporto');
      return;
    }
    // Check that if one doc is uploaded but other isn't, user knows
    if ((fronte && !retro) || (!fronte && retro)) {
      setError('Carica ENTRAMBI fronte e retro del documento per l\'approvazione automatica (oppure non caricare nessuno dei due).');
      return;
    }
    setLoading(true);
    const result = await register({
      ...form,
      codice_fiscale: idType === 'cf' ? form.codice_fiscale : null,
      codice_iss: idType === 'iss' ? form.codice_iss : null,
      numero_passaporto: idType === 'passport' ? form.numero_passaporto : null,
      paese_passaporto: idType === 'passport' ? form.paese_passaporto : null,
      documento_identita: null,
      documento_fronte_id: fronte?.uploaded_id || null,
      documento_retro_id: retro?.uploaded_id || null,
      telefono: form.telefono || null,
    });
    setLoading(false);
    if (result.success) {
      if (result.pending) {
        setPendingState({ email: form.email, message: result.message });
      } else {
        // Auto-approved (uploaded docs) → logged in
        navigate('/');
      }
    } else {
      setError(result.error);
    }
  };

  // Pending approval confirmation screen
  if (pendingState) {
    return (
      <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center px-4 pb-8" data-testid="register-pending-screen">
        <div className="w-full max-w-lg">
          <div className="border border-[#B8D4E8] bg-white p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#FFF7E6] flex items-center justify-center mx-auto mb-4">
              <Clock size={32} className="text-[#FF9F0A]" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-[#0D1B2A] mb-3">
              Registrazione Ricevuta
            </h1>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFF7E6] border border-[#FF9F0A]/30 mb-6">
              <Clock size={12} className="text-[#FF9F0A]" />
              <span className="text-[10px] uppercase tracking-widest text-[#FF9F0A] font-bold">In Attesa di Approvazione</span>
            </div>
            <p className="text-sm text-[#4A6B8A] mb-4 leading-relaxed">
              {pendingState.message || "Il tuo profilo è in attesa di approvazione da parte del super admin."}
            </p>
            <div className="flex items-start gap-3 bg-[#E8F4FD] border border-[#B8D4E8] p-4 text-left mb-6">
              <Mail size={16} className="text-[#0A84FF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold text-[#0D1B2A] mb-1">Controlla la tua email</div>
                <div className="text-[11px] text-[#4A6B8A]">
                  Abbiamo inviato una email di conferma a <strong>{pendingState.email}</strong>. Riceverai un'altra email appena il tuo account sarà attivo.
                </div>
              </div>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all"
              data-testid="register-pending-home-btn"
            >
              <CheckCircle2 size={14} /> Torna alla Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center px-4 pb-8" data-testid="register-page">
      <div className="w-full max-w-lg">
        <div className="border border-[#B8D4E8] bg-white p-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png"
              alt="LIFE LINKE"
              className="h-20 w-auto"
            />
          </div>

          <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-6">
            {t('crea_account')}
          </h1>

          {error && (
            <div className="mb-4 p-3 border border-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-2" data-testid="register-error">
              <AlertTriangle size={14} className="text-[#FF3B30]" />
              <span className="text-xs text-[#FF3B30]">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('nome')} *</label>
                <input
                  value={form.nome}
                  onChange={(e) => update('nome', e.target.value)}
                  required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="register-nome-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('cognome')} *</label>
                <input
                  value={form.cognome}
                  onChange={(e) => update('cognome', e.target.value)}
                  required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="register-cognome-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('email')} *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                required
                className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('password')} *</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                required
                className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                data-testid="register-password-input"
              />
            </div>

            {/* Identity toggle: Codice Fiscale (IT) vs Codice ISS (San Marino) vs Passaporto */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">Identificativo Fiscale *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2" data-testid="id-type-toggle">
                <button
                  type="button"
                  onClick={() => { setIdType('cf'); update('codice_iss', ''); update('numero_passaporto', ''); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-widest border transition-all ${
                    idType === 'cf'
                      ? 'border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]'
                      : 'border-[#B8D4E8] bg-white text-[#4A6B8A] hover:border-[#FF3B30]/50'
                  }`}
                  data-testid="select-cf-btn"
                >
                  <span className="text-base">🇮🇹</span>
                  <span className="text-[10px]">Italia<br/>Codice Fiscale</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setIdType('iss'); update('codice_fiscale', ''); update('numero_passaporto', ''); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-widest border transition-all ${
                    idType === 'iss'
                      ? 'border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]'
                      : 'border-[#B8D4E8] bg-white text-[#4A6B8A] hover:border-[#FF3B30]/50'
                  }`}
                  data-testid="select-iss-btn"
                >
                  <span className="text-base">🇸🇲</span>
                  <span className="text-[10px]">San Marino<br/>Codice ISS</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setIdType('passport'); update('codice_fiscale', ''); update('codice_iss', ''); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-widest border transition-all ${
                    idType === 'passport'
                      ? 'border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]'
                      : 'border-[#B8D4E8] bg-white text-[#4A6B8A] hover:border-[#FF3B30]/50'
                  }`}
                  data-testid="select-passport-btn"
                >
                  <span className="text-base">📔</span>
                  <span className="text-[10px]">Estero<br/>Passaporto</span>
                </button>
              </div>

              {idType === 'cf' && (
                <input
                  value={form.codice_fiscale}
                  onChange={(e) => update('codice_fiscale', e.target.value.toUpperCase())}
                  placeholder="Es. RSSMRA85M01H501Z"
                  maxLength={16}
                  required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                  data-testid="register-cf-input"
                />
              )}
              {idType === 'iss' && (
                <>
                  <input
                    value={form.codice_iss}
                    onChange={(e) => update('codice_iss', e.target.value.toUpperCase())}
                    placeholder="Es. 123456"
                    maxLength={20}
                    required
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                    data-testid="register-iss-input"
                  />
                  <p className="text-[10px] text-[#4A6B8A] mt-1 italic">
                    🇸🇲 Il Codice ISS (Istituto Sicurezza Sociale) è riservato ai cittadini della Repubblica di San Marino.
                  </p>
                </>
              )}
              {idType === 'passport' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select
                      value={form.paese_passaporto}
                      onChange={(e) => update('paese_passaporto', e.target.value)}
                      required
                      className="md:col-span-1 bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                      data-testid="register-passport-country"
                    >
                      <option value="">— Seleziona paese —</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                      ))}
                    </select>
                    <input
                      value={form.numero_passaporto}
                      onChange={(e) => update('numero_passaporto', e.target.value.toUpperCase())}
                      placeholder="Numero passaporto · Es. YA1234567"
                      maxLength={20}
                      required
                      className="md:col-span-2 bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                      data-testid="register-passport-input"
                    />
                  </div>
                  <p className="text-[10px] text-[#4A6B8A] mt-1 italic">
                    📔 Seleziona il paese che ha rilasciato il passaporto e inserisci il numero. Per cittadini esteri, sfollati o senza fissa dimora.
                  </p>
                </>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('telefono')}</label>
              <input
                value={form.telefono}
                onChange={(e) => update('telefono', e.target.value)}
                placeholder="+39 xxx xxx xxxx"
                className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                data-testid="register-phone-input"
              />
            </div>

            {/* Fast-track: optional document upload for instant approval */}
            <div className="border border-dashed border-[#32D74B]/40 bg-[#F0FAF0] p-4">
              <div className="flex items-start gap-2 mb-3">
                <Zap size={16} className="text-[#32D74B] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">
                    Approvazione Istantanea <span className="text-[#32D74B]">(opzionale)</span>
                  </div>
                  <p className="text-[11px] text-[#4A6B8A] mt-1">
                    Carica il fronte e il retro del tuo documento d'identità e sarai <strong>approvato automaticamente</strong>, senza attendere il super admin.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Fronte */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#4A6B8A] mb-1 font-bold">Documento · Fronte</label>
                  {!fronte ? (
                    <button
                      type="button"
                      onClick={() => fronteRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-[#B8D4E8] bg-white text-[#4A6B8A] text-xs uppercase tracking-widest hover:border-[#32D74B] hover:text-[#32D74B] transition-all"
                      data-testid="upload-fronte-btn"
                    >
                      <Upload size={12} /> Carica fronte
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-white border border-[#32D74B]/30 px-3 py-2" data-testid="fronte-uploaded">
                      {fronte.uploading ? <Loader2 size={12} className="animate-spin text-[#4A6B8A]" /> : <FileCheck size={12} className="text-[#32D74B]" />}
                      <span className="text-[11px] text-[#0D1B2A] truncate flex-1">{fronte.file.name}</span>
                      <button type="button" onClick={() => setFronte(null)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1" data-testid="remove-fronte">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                  <input
                    ref={fronteRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => uploadDoc(e.target.files[0], setFronte)}
                    className="hidden"
                    data-testid="fronte-file-input"
                  />
                </div>

                {/* Retro */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#4A6B8A] mb-1 font-bold">Documento · Retro</label>
                  {!retro ? (
                    <button
                      type="button"
                      onClick={() => retroRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-[#B8D4E8] bg-white text-[#4A6B8A] text-xs uppercase tracking-widest hover:border-[#32D74B] hover:text-[#32D74B] transition-all"
                      data-testid="upload-retro-btn"
                    >
                      <Upload size={12} /> Carica retro
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-white border border-[#32D74B]/30 px-3 py-2" data-testid="retro-uploaded">
                      {retro.uploading ? <Loader2 size={12} className="animate-spin text-[#4A6B8A]" /> : <FileCheck size={12} className="text-[#32D74B]" />}
                      <span className="text-[11px] text-[#0D1B2A] truncate flex-1">{retro.file.name}</span>
                      <button type="button" onClick={() => setRetro(null)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1" data-testid="remove-retro">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                  <input
                    ref={retroRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => uploadDoc(e.target.files[0], setRetro)}
                    className="hidden"
                    data-testid="retro-file-input"
                  />
                </div>
              </div>

              {fronte?.uploaded_id && retro?.uploaded_id && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-[#32D74B]/10 border border-[#32D74B]/30" data-testid="autoapprove-banner">
                  <Zap size={12} className="text-[#32D74B]" />
                  <span className="text-[10px] text-[#32D74B] font-bold uppercase tracking-widest">Approvazione automatica attivata</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              {t('registrati')}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-[#6B8AB5]">
            {t('hai_gia_account')}{' '}
            <Link to="/login" className="text-[#FF3B30] hover:underline uppercase tracking-widest font-bold" data-testid="login-link">
              {t('login')}
            </Link>
          </p>
          <div className="mt-2 text-center">
            <Link to="/recupera-password" className="text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest" data-testid="forgot-password-link-reg">
              Password dimenticata?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
